// CSR-2063 — the ACORD form editor: the heart of the "Generate ACORD Form" flow.
//
// The agent has already chosen a form and its policies (CSR-2062). Here they see
// the real paper with AMS's values already in the blanks, correct whatever is
// wrong, and either file the result against the insured (Save) or take a copy
// (Download). This component owns the design spec's state matrix and the
// unsaved-changes warning; drawing the page and positioning the inputs is
// `AcordPdfForm`'s job.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Download, Loader2, Save } from "lucide-react";
import { acordDocumentFileName } from "@shared/acord-forms";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import {
  acordFormPrefillQueryKey,
  acordFormTemplateQueryKey,
  fetchAcordDocumentPreview,
  fetchAcordFormPrefill,
  fetchAcordFormTemplate,
  useSaveAcordDocument,
  type AcordFormValues,
} from "@/lib/api";
import type { AcordFormHandoff } from "./GenerateAcordFormDialog";
import { AcordPdfForm } from "./AcordPdfForm";
import { useAcordPdfDocument } from "./acord-pdf-document";
import { initialAcordFormValues, isAcordFormEdited } from "./acord-editor-values";

// Matches the selection modal's window, so stepping back and forth between the
// two never re-requests the same prefill.
const PREFILL_STALE_MS = 5 * 60 * 1000;

interface Props {
  handoff: AcordFormHandoff;
  insuredId: string;
  // Back, X, or a saved document — the parent drops the handoff and the editor
  // unmounts, so there is no stale state to reset.
  onClose: () => void;
}

export function AcordFormEditorDialog({ handoff, insuredId, onClose }: Props) {
  const { form, request } = handoff;
  const { toast } = useToast();
  const saveMutation = useSaveAcordDocument(insuredId);

  // The selection modal already fetched this into the cache under the same key,
  // so the editor opens on values that are already there. Retry below is a
  // refetch of this query rather than a second way of asking.
  const prefillQuery = useQuery({
    queryKey: acordFormPrefillQueryKey(request),
    queryFn: () => fetchAcordFormPrefill(request),
    staleTime: PREFILL_STALE_MS,
    initialData: handoff.prefill,
    retry: false,
  });

  // The blank paper. One template per form, never per insured, so it is cached for
  // the session.
  const templateQuery = useQuery({
    queryKey: acordFormTemplateQueryKey(form.id),
    queryFn: () => fetchAcordFormTemplate(form.id),
    staleTime: Infinity,
    retry: false,
  });

  const pdf = useAcordPdfDocument(templateQuery.data);

  const [values, setValues] = useState<AcordFormValues | null>(null);
  const [baseline, setBaseline] = useState<AcordFormValues | null>(null);
  const [confirmLeaveOpen, setConfirmLeaveOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const prefill = prefillQuery.data;
  const templateReady = pdf.pages.length > 0;

  // The form opens on the template's own contents with the AMS prefill written
  // over them, and that same set is the baseline every later edit is measured
  // against.
  useEffect(() => {
    if (values !== null || !prefill || !templateReady) return;
    const initial = initialAcordFormValues(pdf.templateText, prefill);
    setValues(initial);
    setBaseline(initial);
  }, [values, prefill, templateReady, pdf.templateText]);

  const isEdited = useMemo(
    () => (values !== null && baseline !== null ? isAcordFormEdited(baseline, values) : false),
    [baseline, values],
  );

  const failure = prefillQuery.error ?? templateQuery.error ?? pdf.error;
  const isPreparing = !failure && values === null;
  const isSaving = saveMutation.isPending;
  const isBusy = isSaving || isDownloading;

  // Stable across renders, so the memoised blanks on a 634-field template are not
  // all re-rendered by a keystroke in one of them.
  const setText = useCallback(
    (name: string, value: string) =>
      setValues((current) =>
        current === null ? current : { ...current, text: { ...current.text, [name]: value } },
      ),
    [],
  );
  const setCheck = useCallback(
    (name: string, checked: boolean) =>
      setValues((current) =>
        current === null ? current : { ...current, checks: { ...current.checks, [name]: checked } },
      ),
    [],
  );
  const setRadio = useCallback(
    (name: string, option: string) =>
      setValues((current) =>
        current === null ? current : { ...current, radios: { ...current.radios, [name]: option } },
      ),
    [],
  );

  const handleRetry = () => {
    if (prefillQuery.error) void prefillQuery.refetch();
    if (templateQuery.error) void templateQuery.refetch();
    // A template that arrived but failed to parse: re-run the parse on the bytes
    // we already hold.
    if (pdf.error && !templateQuery.error) pdf.reload();
  };

  const handleRequestClose = () => {
    if (isBusy) return;
    if (isEdited) {
      setConfirmLeaveOpen(true);
      return;
    }
    onClose();
  };

  const handleDownload = async () => {
    if (values === null) return;
    setIsDownloading(true);
    try {
      const blob = await fetchAcordDocumentPreview(insuredId, request, values);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = acordDocumentFileName(form);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      // Deliberately no baseline reset: a copy on the agent's machine is not a
      // filing, so the unsaved-changes warning still applies afterwards.
    } catch (error) {
      console.error("ACORD document download failed:", error);
      toast({
        variant: "destructive",
        title: "Download failed",
        description:
          error instanceof Error && error.message
            ? error.message
            : "Failed to download document. Please try again.",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSave = async () => {
    if (values === null) return;
    try {
      await saveMutation.mutateAsync({ request, values });
      toast({
        title: "Document Saved",
        description: `${form.documentName} has been saved.`,
      });
      onClose();
    } catch (error) {
      console.error("ACORD document save failed:", error);
      // The modal stays open with the edits intact — Save is retryable.
      toast({
        variant: "destructive",
        title: "Save failed",
        description:
          error instanceof Error && error.message
            ? error.message
            : "Failed to save document. Please try again.",
      });
    }
  };

  return (
    <>
      <Dialog open onOpenChange={(next) => !next && handleRequestClose()}>
        <DialogContent
          className="flex h-[92vh] max-h-[92vh] max-w-5xl flex-col gap-0 p-0"
          data-testid="dialog-acord-form-editor"
        >
          <DialogHeader className="shrink-0 space-y-1 border-b p-6 pb-4 text-left">
            <DialogTitle data-testid="acord-editor-title">{form.documentName}</DialogTitle>
            <DialogDescription>
              Pre-filled from the insured and policy data. Click any field on the form to edit
              it before saving.
            </DialogDescription>
          </DialogHeader>

          <div className="min-h-0 flex-1 overflow-hidden">
            {failure ? (
              <div
                className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center"
                data-testid="acord-editor-error"
                role="alert"
              >
                <p className="text-sm text-destructive">
                  Failed to generate document. Please try again.
                </p>
                <p className="max-w-md text-xs text-muted-foreground">{failure.message}</p>
                <Button
                  variant="outline"
                  className="cursor-pointer"
                  onClick={handleRetry}
                  data-testid="button-acord-editor-retry"
                >
                  Retry
                </Button>
              </div>
            ) : isPreparing ? (
              <div
                className="flex h-full flex-col items-center justify-center gap-3 text-sm text-muted-foreground"
                data-testid="acord-editor-loading"
                role="status"
              >
                <Loader2 className="h-6 w-6 animate-spin" aria-hidden="true" />
                Preparing document...
              </div>
            ) : values === null ? null : (
              <AcordPdfForm
                pages={pdf.pages}
                values={values}
                onTextChange={setText}
                onCheckChange={setCheck}
                onRadioChange={setRadio}
                disabled={isBusy}
              />
            )}
          </div>

          <DialogFooter className="shrink-0 border-t p-4 sm:justify-between">
            <Button
              variant="outline"
              className="cursor-pointer"
              onClick={handleRequestClose}
              disabled={isBusy}
              data-testid="button-acord-editor-back"
            >
              <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                className="cursor-pointer"
                onClick={() => void handleDownload()}
                disabled={isPreparing || failure !== null || isBusy}
                data-testid="button-acord-editor-download"
              >
                {isDownloading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <Download className="mr-2 h-4 w-4" aria-hidden="true" />
                )}
                Download
              </Button>
              <Button
                className="cursor-pointer"
                onClick={() => void handleSave()}
                disabled={isPreparing || failure !== null || isBusy}
                data-testid="button-acord-editor-save"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" aria-hidden="true" />
                    Save
                  </>
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmLeaveOpen} onOpenChange={setConfirmLeaveOpen}>
        <AlertDialogContent data-testid="dialog-acord-unsaved-changes">
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to leave? Your edits will be
              lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="cursor-pointer"
              data-testid="button-acord-keep-editing"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="cursor-pointer bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                setConfirmLeaveOpen(false);
                onClose();
              }}
              data-testid="button-acord-discard-changes"
            >
              Leave
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
